import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation
} from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainer } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainer';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { ErrorBoundary } from 'common/hocs/ErrorBoundary';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserProfileTenant } from 'common/services/api/UserProfileGlobalRoles/UserProfileGlobalRoles.thunk';
import {
    ColumnDefinition,
    ColumnType,
    DrawerEntitiesNames,
    Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { useSnackbar } from 'notistack';

import { UserProfileGlobalRolesDeleteDialog } from './UserProfileGlobalRolesDeleteDialog';
import { selectProcessing,selectUserProfileGlobalRolesByTenantId } from './userProfileGlobalRolesSlice';

const CONTAINER_INLINE_STYLES = {
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 1,
    borderColor: 'info.dark',
    height: 'max-content',
    marginBottom: '1rem',
    width: 'calc(50% - 12px)'
};

const GLOBAL_ROLES_INFO_ICON_TEXT = 'Global Roles provides Users with the ability to view and manage entitlements.';

type UserProfileGlobalListProps = {
    tenant: IUserProfileTenant;
    handleSetEditingRow: () => void;
}

export function UserProfileGlobalList({ tenant, handleSetEditingRow }: Readonly<UserProfileGlobalListProps>) {

    const [sortModel, setSortModel] = usePalmSortModel();

    const params = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const userProfileGlobalRoles = useAppSelector(selectUserProfileGlobalRolesByTenantId(tenant.id));
    const processing = useAppSelector(selectProcessing);
    const shouldDisableLinkDeleteButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.GlobalRoleManager ]);

    useEffect(() => {
        if (userProfileGlobalRoles) {
            return;
        }
        const fetchUserProfileTenantParams = {
            tenantId: tenant.id,
            userId: params?.userId ?? ''
        };
        dispatch(fetchUserProfileTenant(fetchUserProfileTenantParams))
            .unwrap()
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile Global Roles', enqueueSnackbar);
            });
    }, []);

    const handleCreateButtonClick = () => {
        handleSetEditingRow();
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserTenant));
        dispatch(setCurrentOperation(Operations.Edit));
        dispatch(setCurrentDrawerId(DrawerIDs.GlobalRoles));
        dispatch(openDrawer());
    };

    const actionDefinitions =  [
        { action: Operations.Open, disabled: true },
        { action: Operations.Delete, disabled: shouldDisableLinkDeleteButtons }
    ];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'name', columnName: 'Global Role Name', columnType: ColumnType.First, copy: false },
        { columnId: 'actions', columnName: 'Action', size: 0.4, columnType: ColumnType.Action, sortable: false }
    ];

    return (
        <UserProfileEntityContainer
            title='Global Roles'
            isCreateButtonDisabled={shouldDisableLinkDeleteButtons}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
            useTitleInfoIcon
            iconTooltipText={GLOBAL_ROLES_INFO_ICON_TEXT}
        >
            <ErrorBoundary>
                <Box sx={{ margin: '0 24px 24px 24px' }}>
                    <UserProfilesDataGrid
                        tenant={tenant}
                        rows={userProfileGlobalRoles}
                        actionDefinitions={actionDefinitions}
                        columnDefinitions={columnDefinitions}
                        processing={processing === tenant.id}
                        title='Global Roles'
                        deleteFunction={UserProfileGlobalRolesDeleteDialog}
                        customDeleteTitle={'Are you sure you want to remove this Global Role from this User?'}
                        currentEntityType={DrawerEntitiesNames.UserGlobalRoles}
                        sortModel={sortModel}
                        clientSortHandler={setSortModel}
                    />
                </Box>
            </ErrorBoundary>
        </UserProfileEntityContainer>
    );
}
